import { feature } from '@turf/helpers'
import union from '@turf/union'
import { Feature } from 'ol'
import Collection from 'ol/Collection'
import { GeoJSON } from 'ol/format'
import { Geometry, GeometryCollection } from 'ol/geom'
import BaseLayer from 'ol/layer/Base'
import Vector from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import { Fill, Stroke, Style } from 'ol/style'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppSelector } from '../../common/store'
import {DEFAULT_LAYERS, lotBoundaryLayer, mapproxyOSMLayer} from '../map/map.constants'
import {APStyledLayerType} from './ap.slice'
import { selectCartItems } from './apCart.slice'
import { selectHoverFrame, selectSelectedFrame, selectVisibleFrames } from './apFrames.slice'
import {selectDrawnPolygon} from "./apDrawPoygonSlice";
import {useMap} from "../map/map.hook";
import {OPEN_LAYERS_MAPPROXY_OSM_LAYER_NAME} from "../../environment";
import {Draw, Snap} from "ol/interaction";

const style = new Style({
  stroke: new Stroke({
    color: 'rgb(160, 32, 240, 1)',
    width: 1,
  }),
  fill: new Fill({
    color: 'rgba(160, 32, 240, 0.0)',
  }),
})

const cartStyle = new Style({
  stroke: new Stroke({
    color: 'rgb(35,110,11)',
    width: 2,
  }),
  fill: new Fill({
    color: 'rgba(35,110,11,0.2)',
  }),
})

const selectedStyle = new Style({
  stroke: new Stroke({
    color: 'rgb(240,112,32)',
    width: 2,
  }),
  fill: new Fill({
    color: 'rgba(240,112,32,0.2)',
  }),
})

const hoverStyle = new Style({
  stroke: new Stroke({
    color: 'rgb(212,32,240)',
    width: 4,
  }),
  fill: new Fill({
    color: 'rgba(212,32,240, 0.2)',
  }),
})

const drawnPolygonStyle = new Style({
  stroke: new Stroke({
    color: 'rgb(240,112,32)',
    width: 2,
  }),
})

const useApMapData = () => {
  const drawnPolygon = useAppSelector(selectDrawnPolygon)
  const visibleFrames = useAppSelector(selectVisibleFrames)
  const selectedFrame = useAppSelector(selectSelectedFrame)
  const hoverFrame = useAppSelector(selectHoverFrame)

  const cartFrames = useAppSelector(selectCartItems)

  const source = new VectorSource({wrapX: false, });

  const [draw, setDraw] = useState(() => {return newDraw() })
  const [snap, setSnap ] = useState(() => {return newSnap()} )
  const { layers, setLayers } = useMap()

  function newDraw() {
    return new Draw({ source: source,
      type: 'Polygon', stopClick: false, geometryName: 'userDrawn',
    })
  }

  function newSnap() {
    return new Snap({source: source})
  }

  const buildLayer = useCallback((styledLayer: APStyledLayerType, featureIdPrefix: string) => {
    const geoJSON = new GeoJSON()
    const layerFeaturesCollection = new Collection<Feature>()
    const styledLayerSetData = styledLayer.data

    let styledLayerFeatures: Feature<Geometry>[] = []
    styledLayerSetData.forEach(
      (styleData) => {
        if (styleData.geometry) {
          let geometries: Geometry[] = []
          geometries.push(geoJSON.readGeometry(styleData.geometry).transform('EPSG:4326', 'EPSG:3857'))
          const hasLocation = true
          const layerFeature = new Feature({
            fillColor: styledLayer.fillColor,
            geometry: new GeometryCollection(geometries),
            name: `${featureIdPrefix}-${styleData.id}`,
            id: `${featureIdPrefix}-${styleData.id}`,
            hasLocation: hasLocation,
          })
          styledLayerFeatures.push(layerFeature)
        }
      })

    if (styledLayerFeatures.length > 0) {
      layerFeaturesCollection.extend(styledLayerFeatures)
    }

    return new Vector({
      source: new VectorSource({
          features: layerFeaturesCollection,
        },
      ),
      style: styledLayer.style,
      className: styledLayer.className,
      properties: {
        name: styledLayer.className,
      },
    })
  }, [])

  const framesLayer = useMemo(() => {
    if (visibleFrames.length > 0) {
      const styledLayer: APStyledLayerType = {
        'fillColor': 'rgba(160, 32, 240, 0.2)',
        'type': undefined,
        'style': style,
        'className': 'apLayer-frames',
        'data': visibleFrames,
      }

      return buildLayer(styledLayer, 'frames')
    }
  }, [buildLayer, visibleFrames])

  const cartFramesLayer = useMemo(() => {
    if (cartFrames.length > 0) {

      const combinedGeometry = cartFrames.filter(value => !!value.frame.geometry)
        .map(value => feature(value.frame.geometry!))
        .reduce((previousValue, currentValue) => {
          const unionFeature = union(previousValue, currentValue)
          if (unionFeature !== null) {
            return unionFeature
          }
          return currentValue
        }).geometry

      const styledLayer: APStyledLayerType = {
        'fillColor': 'rgba(160, 32, 240, 0.2)',
        'type': undefined,
        'style': cartStyle,
        'className': 'apLayer-frames-cart',
        'data': [{ ...cartFrames[0].frame, geometry: combinedGeometry }],
      }

      return buildLayer(styledLayer, 'frames-cart')
    }
  }, [buildLayer, cartFrames])

  const selectedFrameLayer = useMemo(() => {
    if (selectedFrame) {
      const styledLayer: APStyledLayerType = {
        'fillColor': 'rgba(160, 32, 240, 0.2)',
        'type': undefined,
        'style': selectedStyle,
        'className': 'apLayer-frames-selected',
        'data': [selectedFrame],
      }

      return buildLayer(styledLayer, 'frames-selected')
    }
  }, [buildLayer, selectedFrame])

  const hoverFrameLayer = useMemo(() => {
    if (hoverFrame) {
      const styledLayer: APStyledLayerType = {
        'fillColor': 'rgba(160, 32, 240, 0.2)',
        'type': undefined,
        'style': hoverStyle,
        'className': 'apLayer-frames-hover',
        'data': [hoverFrame],
      }

      return buildLayer(styledLayer, 'frames-hover')
    }
  }, [buildLayer, hoverFrame])

  const drawnPolygonLayer = useMemo( () => {
    if (drawnPolygon) {
      const styledLayer: APStyledLayerType = {
        'fillColor': 'rgba(0, 0, 0, 1.0)',
        'type': undefined,
        'style': drawnPolygonStyle,
        'className': 'apLayer-drawn-polygon',
        'data': [{ ...visibleFrames[0], geometry: drawnPolygon }]
      }
      return buildLayer(styledLayer, 'drawn-polygon')
    }
  }, [buildLayer, drawnPolygon])

  useEffect(() => {
    if(draw && drawnPolygonLayer)
    {
      draw.set("source", drawnPolygonLayer)
    }
  }, [drawnPolygonLayer, draw])

  useEffect(() => {
    const newLayers: BaseLayer[] = []

    if (framesLayer) {
      newLayers.push(framesLayer)
    }
    if (cartFramesLayer) {
      newLayers.push(cartFramesLayer)
    }
    if (selectedFrameLayer) {
      newLayers.push(selectedFrameLayer)
    }
    if (hoverFrameLayer) {
      newLayers.push(hoverFrameLayer)
    }
    if (drawnPolygonLayer) {
      newLayers.push(drawnPolygonLayer)
    }

    setLayers ([...DEFAULT_LAYERS, ...newLayers])

  }, [framesLayer, cartFramesLayer, selectedFrameLayer, hoverFrameLayer, drawnPolygonLayer])

  return {
    layers,
    draw,
    snap,
    visibleFrames,
    selectedFrame,
  }
}

export { useApMapData }